import {
  SourceBundleMediaType,
  canonicalApprovalActionContext,
  canonicalizeJson,
  type ApprovalActionContext,
  type ApprovalRequestedPayload,
  type RunResponse,
} from "@carbon-operations/contracts";

/** Browser-side SHA-256 of the exact normalized object the server hashes. */
export async function approvalActionContextSha256(context: ApprovalActionContext): Promise<string> {
  const bytes = new TextEncoder().encode(canonicalizeJson(canonicalApprovalActionContext(context)));
  const digest = await globalThis.crypto.subtle.digest("SHA-256", bytes);
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, "0")).join("");
}

/** Any hashing or validation failure is an integrity mismatch, never approval evidence. */
export async function approvalActionContextDigestMatches(approval: ApprovalRequestedPayload): Promise<boolean> {
  try {
    return await approvalActionContextSha256(approval.action_context) === approval.expected_action_sha256;
  } catch {
    return false;
  }
}

/** Capability-specific facts that must agree with the acknowledged run. */
export function approvalInputsAndExecutionMatchRun(
  context: ApprovalActionContext,
  run: RunResponse,
): boolean {
  if (context.tenant_id !== run.tenant_id || context.workspace_id !== run.workspace_id) return false;
  if (context.capability_id === "ops.document.draft.v1") {
    const input = context.inputs[0];
    return context.inputs.length === 1 && input?.name === "source_bundle" &&
      input.media_type === SourceBundleMediaType && context.requested_autonomy === "draft" &&
      context.execution.kind === "document_draft" &&
      context.execution.template_version === "operations-summary-v1" &&
      context.execution.output_media_type === "text/markdown";
  }
  if (context.capability_id === "ops.agent.action.v1") {
    return context.inputs.length === 0 && context.requested_autonomy === "act_with_approval" &&
      context.execution.kind === "authorization_receipt" &&
      context.execution.effect === "OperationSV authorizes this request and issues a receipt; it does not execute the action.";
  }
  return context.execution.kind === "capability_request";
}
